// Модель цены сделки — единый источник расчётов для шапки (DealHeader), панели цены (DealPrice) и
// итога расчёта (DealFinalSettlement). Чистые функции (без React).
//
// Бизнес-решение: в UI ВСЕ суммы — БЕЗ НДС; сам НДС присутствует только в документах (счета, УПД),
// поэтому в модели НДС не выделяем. Берём без-НДС поля бэка (*WithoutNds). «Доставка» отдельного
// без-НДС поля у бэка не имеет (logisticsTotalPrice — gross-смешанный), поэтому считаем её из без-НДС
// ставок × вес: (транспортировка + экспедитор + комиссия) × вес. Состав логистики — по order-service
// BargainCalculationUtils.calculateLogisticPricePerTon (продавцу = 0).
import type { BargainStepsInfo } from '@/types/deals';

/** Сумма за товар (без НДС): значение (план или факт) + флаг факта + плановое значение для справки. */
export interface GoodsTotal {
  readonly amount: number | undefined;
  /** true → значение фактическое (из finalReferenceInfo); planAmount — план для справки. */
  readonly actual: boolean;
  readonly planAmount: number | undefined;
}

export interface DealPriceModel {
  /** Упрощённый вид продавца (видит только цену товара и окончательную цену продажи). */
  readonly sellerView: boolean;
  /** Сумма за товар по FCA (чистый товар без доставки) — для шапки, продавца и строки «Товар» у
   *  покупателя. Все суммы без НДС. Доставку покупатель видит отдельной строкой; CPT = FCA + доставка. */
  readonly goodsFca: GoodsTotal;
  readonly fcaPerTon: number | undefined;
  /** Доставка (логистика), всего без НДС. undefined → доставки нет (FCA без ТЭО) или нет данных. */
  readonly logisticsTotal: number | undefined;
  /** Под-части логистики (ставки ₽/т, без НДС). undefined — нет соответствующей составляющей. */
  readonly transportationPerTon: number | undefined;
  readonly forwarderPerTon: number | undefined;
  readonly commissionPerTon: number | undefined;
  /** Итого по сделке (покупатель/агент) = Товар(FCA) + Доставка, без НДС (это и есть CPT). undefined — у продавца. */
  readonly grandTotal: number | undefined;
}

const round2 = (x: number): number => Math.round(x * 100) / 100;

/** Доставка (логистика) без НДС, РОЛЬ-НЕЗАВИСИМО: транспортировка + экспедитор + комиссия (без-НДС
 *  ставки) × вес (delivered, иначе planned). logisticsTotalPrice бэка — gross-смешанный, не годится.
 *  Используется и для панели цены (там гейтится ролью продавца), и для суммы счёта за логистику. */
export function logisticsTotalWithoutNds(info: BargainStepsInfo): number | undefined {
  const p = info.payment ?? {};
  const weight = info.deliveredWeight ?? info.plannedWeight;
  if (weight == null) {
    return undefined;
  }
  const rates = [p.transportationPriceWithoutNds, p.forwarderFeePerTonWithoutNds, p.serviceFeePerTonWithoutNds].filter(
    (x): x is number => x != null && x > 0,
  );
  return rates.length === 0 ? undefined : round2(rates.reduce((s, r) => s + r, 0) * weight);
}

/** Фактическая стоимость товара без НДС из окончательного расчёта (по реальному весу/качеству). */
function actualGoodsWithoutNds(info: BargainStepsInfo): number | undefined {
  const fr = info.finalReferenceInfo;
  if (fr?.totalAmount == null) {
    return undefined;
  }
  return fr.totalAmount - (fr.totalNds ?? 0);
}

/** Стоимость товара без НДС для счёта за товар: факт (finalReferenceInfo) либо план (FCA). */
export function goodsTotalWithoutNds(info: BargainStepsInfo): number | undefined {
  return actualGoodsWithoutNds(info) ?? info.payment?.fullPriceFCAWithoutNds ?? undefined;
}

function goodsTotal(planAmount: number | undefined, actual: number | undefined): GoodsTotal {
  if (actual != null) {
    return { amount: actual, actual: true, planAmount };
  }
  return { amount: planAmount, actual: false, planAmount: undefined };
}

/** Итог по товару для шапки: цена по спецификации (план), факт и разница (факт − план), без НДС. */
export interface GoodsOutcome {
  readonly spec: number | undefined;
  /** undefined → сделка ещё не завершена (факта/разницы нет). */
  readonly fact: number | undefined;
  readonly diff: number | undefined;
}

export function goodsSpecFactDiff(info: BargainStepsInfo): GoodsOutcome {
  const g = buildDealPriceModel(info).goodsFca;
  if (!g.actual) {
    return { spec: g.amount, fact: undefined, diff: undefined };
  }
  const spec = g.planAmount;
  const fact = g.amount;
  const diff = spec != null && fact != null ? round2(fact - spec) : undefined;
  return { spec, fact, diff };
}

/** Итог расчёта по факту поставки: доплата (факт > план), переплата/возврат (факт < план) или закрыто. */
export type SettlementKind = 'underpayment' | 'overpayment' | 'settled';

export interface Settlement {
  readonly kind: SettlementKind;
  /** Сумма к доплате/возврату (модуль разницы факт−план, без НДС — как «Разница» в шапке).
   *  Это денежный перевод, не цена реализации, поэтому НДС в нём не выделяем. */
  readonly amount: number;
}

/** Доплата/переплата как разница факт (finalReferenceInfo) − план (спецификация, fullPriceFCA).
 *  null → окончательного расчёта ещё нет (нет finalReferenceInfo). Порог 1 ₽ — near-zero = «закрыто». */
export function buildSettlement(info: BargainStepsInfo): Settlement | null {
  const fr = info.finalReferenceInfo;
  const p = info.payment ?? {};
  if (fr?.totalAmount == null || p.fullPriceFCAWithoutNds == null) {
    return null;
  }
  const factWithoutNds = fr.totalAmount - (fr.totalNds ?? 0);
  const diff = round2(factWithoutNds - p.fullPriceFCAWithoutNds);
  const kind: SettlementKind = diff > 1 ? 'underpayment' : diff < -1 ? 'overpayment' : 'settled';
  return { kind, amount: Math.abs(diff) };
}

export function buildDealPriceModel(info: BargainStepsInfo): DealPriceModel {
  const p = info.payment ?? {};
  const actual = actualGoodsWithoutNds(info);

  const goodsFca = goodsTotal(p.fullPriceFCAWithoutNds, actual);

  const sellerView = info.isSeller === true;

  const transportationPerTon = sellerView ? undefined : p.transportationPriceWithoutNds;
  const forwarderPerTon = sellerView ? undefined : p.forwarderFeePerTonWithoutNds;
  const commissionPerTon = sellerView ? undefined : p.serviceFeePerTonWithoutNds;

  // В панели цены логистику показываем только покупателю/агенту; расчёт — общий (роль-независимый).
  const logisticsTotal = sellerView ? undefined : logisticsTotalWithoutNds(info);

  // Итого = Товар(FCA) + Доставка (это и есть CPT). Берём FCA, а НЕ fullPriceCPT, иначе доставка
  // задвоится (CPT уже включает доставку/экспедирование/комиссию).
  const grandTotal =
    sellerView || goodsFca.amount == null ? undefined : round2(goodsFca.amount + (logisticsTotal ?? 0));

  return {
    sellerView,
    goodsFca,
    fcaPerTon: p.fcaPricePerTonWithoutNds,
    logisticsTotal,
    transportationPerTon,
    forwarderPerTon,
    commissionPerTon,
    grandTotal,
  };
}
